import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/Modal";
import { useToast } from "@/components/Toast";
import Button from "@/components/ui/Button";
import type { AUTProjectTank } from "@/api/queries/account";
import { refetchDataAtom } from "@/store";
import {
  projectByIdAtom,
  propertyDevicesAtom,
  virtualDeviceConfigurationAtom,
  waterPumpVirtualDeviceConfigMapAtom,
} from "@/store/data";
import {
  createVirtualDeviceConfigurationAtom,
  updateVirtualDeviceConfigurationAtom,
  updateProjectTankAtom,
} from "@/store/crud";
import { filterAvailableOutputs } from "@/utils/device";
import { resolveFertigationDevice } from "@/utils/fertigation-device";
import { getDeviceModelCommercialName } from "@/utils/device-model";
import { isMeshDevice } from "@/utils/mesh-device-utils";

interface EditTankControllerDialogProps {
  projectTank: AUTProjectTank | null;
  isOpen: boolean;
  onClose: () => void;
}

function EditTankControllerDialog({ projectTank, isOpen, onClose }: EditTankControllerDialogProps) {
  const { showWarning } = useToast();

  const getProjectById = useAtomValue(projectByIdAtom);
  const propertyDevices = useAtomValue(propertyDevicesAtom);
  const virtualDeviceConfigs = useAtomValue(virtualDeviceConfigurationAtom);
  const waterPumpVdcMap = useAtomValue(waterPumpVirtualDeviceConfigMapAtom);
  const createVirtualDeviceConfiguration = useSetAtom(createVirtualDeviceConfigurationAtom);
  const updateVirtualDeviceConfiguration = useSetAtom(updateVirtualDeviceConfigurationAtom);
  const updateProjectTank = useSetAtom(updateProjectTankAtom);
  const refetchData = useSetAtom(refetchDataAtom);

  const activeVdc = useMemo(() => {
    if (!projectTank) return null;
    return projectTank.vdc_configurations.find((v) => !v.end_date) ?? null;
  }, [projectTank]);

  // Single-device fertigation rule resolution for the tank's project.
  //
  // The controller is locked to the resolved device EXCEPT in the special case
  // of Case B (no fertigation pump) with exactly one active project_tank: that
  // single tank may be moved to any PumpLink, since there is no sibling to
  // conflict with. Case A is always locked (the fertigation pump defines the
  // device). Per-tank reassignment only.
  const project = useMemo(
    () => (projectTank ? getProjectById(projectTank.project) : null),
    [getProjectById, projectTank]
  );
  const resolved = useMemo(() => resolveFertigationDevice(project, waterPumpVdcMap), [project, waterPumpVdcMap]);
  const lockedDeviceId = resolved.case === "A" || resolved.activeTankCount >= 2 ? resolved.deviceId : null;

  const [controllerId, setControllerId] = useState<string>(lockedDeviceId ?? activeVdc?.device ?? "");
  const [outputIndex, setOutputIndex] = useState<number | null>(activeVdc?.turn_on_physical_output_index ?? null);
  const [flowRateLh, setFlowRateLh] = useState<string>(projectTank?.flow_rate_lh?.toString() ?? "");
  const [saving, setSaving] = useState(false);

  // Keep the controller in sync with the enforced device whenever resolution
  // changes AND the device is actually locked (Case A, or Case B with >= 2
  // tanks). When unlocked (Case B single-tank), leave the user's choice alone.
  useEffect(() => {
    if (lockedDeviceId) setControllerId(lockedDeviceId);
  }, [lockedDeviceId]);

  const allMeshControllers = useMemo(
    () => propertyDevices.map((pd) => pd.device).filter((d) => isMeshDevice(d.model)),
    [propertyDevices]
  );

  // Only the enforced device is selectable when locked. When unlocked (the
  // Case B single-tank special case) any mesh device is selectable so the user
  // can move the only tank to a different PumpLink.
  const selectableControllers = useMemo(() => {
    if (lockedDeviceId) {
      return allMeshControllers.filter((d) => d.id === lockedDeviceId);
    }
    return allMeshControllers;
  }, [allMeshControllers, lockedDeviceId]);

  const otherVdcs = useMemo(
    () => virtualDeviceConfigs.filter((vdc) => vdc.id !== activeVdc?.id),
    [virtualDeviceConfigs, activeVdc?.id]
  );

  const availableOutputs = useMemo(
    () => filterAvailableOutputs(allMeshControllers, otherVdcs),
    [allMeshControllers, otherVdcs]
  );

  const currentOutputs = useMemo(
    () => availableOutputs.find((ao) => ao.device.id === controllerId)?.outputs ?? [],
    [availableOutputs, controllerId]
  );

  // Warn when the project's tank VDCs are split across devices (should not
  // happen under the rule, but may exist historically).
  useEffect(() => {
    if (isOpen && resolved.existingTankDeviceIds.length > 1) {
      showWarning({
        message:
          "Os tanques deste projeto estão em mais de um controlador. Todos os tanques devem usar o mesmo PumpLink.",
      });
    }
  }, [isOpen, resolved.existingTankDeviceIds.length, showWarning]);

  const hasChanges =
    controllerId !== (activeVdc?.device ?? "") ||
    outputIndex !== (activeVdc?.turn_on_physical_output_index ?? null) ||
    flowRateLh !== (projectTank?.flow_rate_lh?.toString() ?? "");

  const canSave = controllerId !== "" && outputIndex != null && hasChanges && !saving;

  const handleSave = async () => {
    if (!projectTank || !canSave) return;
    setSaving(true);
    try {
      if (activeVdc) {
        await updateVirtualDeviceConfiguration({
          id: activeVdc.id,
          data: { end_date: new Date().toISOString() } as any,
        });
      }

      await createVirtualDeviceConfiguration({
        device: controllerId,
        mode: "continuous" as const,
        turn_on_physical_output_index: outputIndex!,
        equipment_type: "tank" as any,
        project_tank: projectTank.id,
        start_date: new Date().toISOString(),
      } as any);

      const newFlowRate = parseFloat(flowRateLh);
      if (!isNaN(newFlowRate) && newFlowRate !== projectTank.flow_rate_lh) {
        await updateProjectTank({
          id: projectTank.id,
          data: { flow_rate_lh: newFlowRate },
        });
      }

      await refetchData();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  const controllerLocked = lockedDeviceId !== null;
  const lockedControllerLabel = (() => {
    const dev = allMeshControllers.find((d) => d.id === controllerId);
    if (!dev) return "";
    return dev.metadata?.label ? `${dev.metadata.label} - ${dev.identifier}` : dev.identifier;
  })();

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Controlador — ${projectTank?.tank.label ?? ""}`} size="md">
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Controlador (PumpLink)</label>
          <select
            value={controllerId}
            onChange={(e) => {
              setControllerId(e.target.value);
              setOutputIndex(null);
            }}
            disabled={controllerLocked}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none bg-white disabled:bg-neutral-100 disabled:text-neutral-500"
          >
            <option value="">
              {controllerLocked ? lockedControllerLabel || "Selecione o controlador" : "Selecione o controlador"}
            </option>
            {selectableControllers.map((device) => (
              <option key={device.id} value={device.id}>
                {device.metadata?.label ? `${device.metadata.label} - ${device.identifier}` : device.identifier} -{" "}
                {getDeviceModelCommercialName(device.model)}
              </option>
            ))}
          </select>
          {controllerLocked ? (
            <p className="text-xs text-neutral-400 mt-1">
              {resolved.case === "A"
                ? "Definido pela bomba de fertirrigação do projeto."
                : "Definido pelo controlador compartilhado entre os tanques do projeto."}
            </p>
          ) : (
            <p className="text-xs text-neutral-400 mt-1">Único tanque do projeto: o controlador pode ser alterado.</p>
          )}
        </div>

        {controllerId && (
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Saída de Ativação</label>
            <select
              value={outputIndex ?? ""}
              onChange={(e) => setOutputIndex(Number(e.target.value))}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none bg-white"
            >
              <option value="">Selecione a saída</option>
              {currentOutputs.map((output) => (
                <option key={output} value={output} disabled={output > 6}>
                  {output}
                  {output > 6 ? " (máx. 6)" : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Vazão (L/h)</label>
          <input
            type="number"
            min="0"
            step="0.1"
            value={flowRateLh}
            onChange={(e) => setFlowRateLh(e.target.value)}
            placeholder="0"
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none bg-white"
          />
        </div>

        <div className="flex gap-4 pt-4 border-t border-neutral-200">
          <Button onClick={handleSave} loading={saving} disabled={!canSave} variant="primary" className="flex-1">
            Salvar
          </Button>
          <Button onClick={handleClose} disabled={saving} variant="secondary" className="flex-1">
            Cancelar
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default EditTankControllerDialog;
